import { useState } from 'react';
import { type PreconfiguredResponse } from '@rsos/reduxDataStore/src/features/preconfiguredMessages/preconfiguredMessagesAPI';
import { type SmartPhraseContext } from '@rsos/utils/SmartPhrases';
import {
  trackLocationRequestClicked,
  trackLocationRequestFailed,
  type LocationRequestPathway,
} from '../../../gainsightTrackings/locationRequestTrackings';
import { resolveTrackedLocationRequestMessage } from '../../PreconfiguredMessages/resolveLocationRequestMessage';

type ChatWithMessageField = {
  messageField: { setText: (text: string) => void };
};

interface UseRequestLocationArgs {
  chat: ChatWithMessageField | null | undefined;
  isConversationEnded: boolean;
  preconfiguredMessages: PreconfiguredResponse[] | undefined;
  /** The text pathway this chat sends over; required so no caller goes untracked. */
  pathwayType: LocationRequestPathway;
  conversationId: string | undefined;
}

export const useRequestLocation = ({
  chat,
  isConversationEnded,
  preconfiguredMessages,
  pathwayType,
  conversationId,
}: UseRequestLocationArgs) => {
  const [locationError, setLocationError] = useState(false);

  const handleRequestLocation = async (locationData: SmartPhraseContext) => {
    setLocationError(false);

    const trackingContext = {
      pathwayType,
      callerID: locationData.callerID,
      conversationId,
    };

    // Tracked before the guard so intent is counted even when the chat isn't in
    // a state that can accept the message.
    trackLocationRequestClicked(trackingContext);

    if (!chat || isConversationEnded || !preconfiguredMessages) {
      trackLocationRequestFailed({
        ...trackingContext,
        failureReason: 'chat_unavailable',
      });
      return;
    }

    const resolvedMessage = await resolveTrackedLocationRequestMessage(
      preconfiguredMessages,
      locationData,
      trackingContext,
    );

    if (!resolvedMessage) {
      setLocationError(true);
      return;
    }

    chat.messageField.setText(resolvedMessage.body);
  };

  return { handleRequestLocation, locationError };
};
